import React, { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import { useNavigate } from "react-router-dom";
import API_URL from "../apiConfig";

function formatDate(value) {
  if (!value) return "Sin fecha";

  return new Date(value).toLocaleDateString("es-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function getDateInputValue(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
}

function getEntryCategory(entry) {
  const sourceType = entry?.source_type || "";
  const sourceModule = entry?.source_module || "";

  if (sourceType === "connection_wall_snapshot") {
    return "connection_wall_snapshot";
  }
  if (sourceType === "micro_mission") return "micro_mission";
  if (sourceType === "word" || sourceType === "reflection") return "word";
  if (sourceType === "conexion" || sourceType === "prayer" || sourceModule === "conexion") {
    return "conexion";
  }
  if (sourceType === "guide" || sourceModule === "guide") return "guide";

  return "all";
}

function getEntryCategoryLabel(entry) {
  const category = getEntryCategory(entry);

  if (category === "micro_mission") return "Micro-misión";
  if (category === "word") return "La Palabra en lo cotidiano";
  if (category === "connection_wall_snapshot") return "Conexión";
  
  return "Registro ZEDRO";
}

function getEntrySelectLabel(entry) {
  const type = getEntryCategoryLabel(entry);
  const day = entry.lesson_day_number ? `Día ${entry.lesson_day_number}` : "Registro";
  const title = entry.lesson_title || entry.title || "Bitácora ZEDRO";

  return `${type} · ${day} · ${title}`;
}

function getBlockLabel(kind) {
  if (kind === "rule") return "Recordatorio del día";
  if (kind === "micro_mission") return "Micro-misión";
  if (kind === "reflection" || kind === "word") return "La Palabra en lo cotidiano";
  if (kind === "conexion" || kind === "prayer") return "Conexión";
  if (kind === "guide") return "Guía";

  return "Pregunta / Acción";
}

function renderFormattedText(value) {
  const text = String(value || "").trim();

  if (!text) return null;

  const paragraphs = text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return paragraphs.map((paragraph, paragraphIndex) => {
    const lines = paragraph.split("\n");

    return (
      <p
        key={paragraphIndex}
        style={{
          margin: paragraphIndex === 0 ? 0 : "0.95rem 0 0",
        }}
      >
        {lines.map((line, lineIndex) => {
          const parts = line.split(/(\*\*[^*]+\*\*)/g);

          return (
            <React.Fragment key={lineIndex}>
              {lineIndex > 0 && <br />}

              {parts.map((part, index) => {
                if (part.startsWith("**") && part.endsWith("**")) {
                  return (
                    <strong key={index}>
                      {part.replaceAll("**", "")}
                    </strong>
                  );
                }

                return <React.Fragment key={index}>{part}</React.Fragment>;
              })}
            </React.Fragment>
          );
        })}
      </p>
    );
  });
}

function renderConnectionWallSnapshot(entry) {
  const snapshot = entry?.response || {};

  const columns = [
    {
      key: "battle_plan",
      title: "Plan de Batalla",
      subtitle: "Verdades bíblicas que sostienen tu oración.",
      items: snapshot.battle_plan || [],
    },
    {
      key: "petitions",
      title: "Peticiones activas",
      subtitle: "Lo que presentaste delante de Dios.",
      items: snapshot.petitions || [],
    },
    {
      key: "prayers",
      title: "Oraciones",
      subtitle: "Palabras ordenadas desde tu proceso.",
      items: snapshot.prayers || [],
    },
    {
      key: "victories",
      title: "Victorias",
      subtitle: "Respuestas que fortalecen tu memoria.",
      items: snapshot.victories || [],
    },
  ];

  function renderSnapshotCard(card) {
    return (
      <article key={card.id} className="bitacoraWallCard">
        <div className="bitacoraWallCardTop">
          {card.category && <span>{card.category}</span>}
        </div>

        {card.title && <h4>{card.title}</h4>}

        {card.scripture_reference && (
          <p className="bitacoraWallReference">{card.scripture_reference}</p>
        )}

        {card.scripture_text && (
          <p className="bitacoraWallText">{card.scripture_text}</p>
        )}

        {card.content && (
          <p className="bitacoraWallText">{card.content}</p>
        )}

        {card.prayer_text && (
          <div className="bitacoraWallPrayer">
            <strong>Oración</strong>
            <p>{card.prayer_text}</p>
          </div>
        )}

        {card.answered_note && (
          <div className="bitacoraWallVictory">
            <strong>Victoria registrada</strong>
            <p>{card.answered_note}</p>
          </div>
        )}
      </article>
    );
  }

  return (
    <div className="bitacoraWallSnapshot">
      <div className="bitacoraWallSnapshotHead">
        <span>Cuarto de Guerra</span>
      </div>

      <div className="bitacoraWallGrid">
        {columns.map((column) => (
          <section
            key={column.key}
            className={`bitacoraWallColumn bitacoraWallColumn--${column.key}`}
          >
            <div className="bitacoraWallColumnHead">
              <h4>{column.title}</h4>
              <p>{column.subtitle}</p>
            </div>

            <div className="bitacoraWallCards">
              {column.items.length > 0 ? (
                column.items.map((card) => renderSnapshotCard(card))
              ) : (
                <p className="bitacoraWallEmpty">Sin registros en esta sección.</p>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function renderResponse(response, entry) {
  if (entry?.source_type === "connection_wall_snapshot") {
    return renderConnectionWallSnapshot(entry);
  }

  if (!response || typeof response !== "object") {
    return <p className="bitacoraEmptyText">No hay respuestas registradas.</p>;
  }

  if (Array.isArray(response.journal_entries) && response.journal_entries.length > 0) {
    return (
      <div className="bitacoraResponseGrid">
        {response.journal_entries.map((item, index) => {
          const label = getBlockLabel(item.kind);

          return (
            <div
              key={item.block_id || index}
              className={`bitacoraResponseCard ${
                item.kind === "rule" ? "is-rule" : ""
              }`}
            >
              <div className="bitacoraResponseLabel">{label}</div>

              <div className="bitacoraResponseQuestion">
                {renderFormattedText(item.question)}
              </div>

              <div className="bitacoraResponseAnswer">
                {renderFormattedText(item.answer)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return <p className="bitacoraEmptyText">No hay respuestas visibles registradas.</p>;
}

function cleanPdfText(value) {
  return String(value || "")
    .replace(/\*\*/g, "")
    .replace(/\[\[SCRIPTURE_REFERENCE\]\]/g, "")
    .replace(/\[\[BULLET:dot\]\]/g, "• ")
    .replace(/\[\[QUOTE\]\]/g, "")
    .replace(/\[\[ICON:[^\]]+\]\]/g, "")
    .replace(/\r\n/g, "\n")
    .trim();
}

function buildPdfFileName(entry) {
  const category = getEntryCategory(entry);
  const day = entry?.lesson_day_number ? `dia-${entry.lesson_day_number}` : "registro";

  const type =
    category === "word"
      ? "palabra-en-lo-cotidiano"
      : category === "micro_mission"
      ? "micro-mision"
      : category;

  return `ZEDRO-Academy-${type}-${day}.pdf`;
}

export default function Bitacora() {
  const navigate = useNavigate();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [noteMessage, setNoteMessage] = useState("");

  useEffect(() => {
    loadJournal();
  }, []);

  async function loadJournal() {
    try {
      const session = JSON.parse(localStorage.getItem("zedro_session"));

      const res = await fetch(`${API_URL}/api/journal`, {
        headers: {
          Authorization: `Bearer ${session?.token}`,
        },
      });

      const json = await res.json();

      if (json.ok) {
        setEntries(json.entries || []);
      }
    } catch (err) {
      console.error("[BITACORA_LOAD_ERROR]", err);
    } finally {
      setLoading(false);
    }
  }

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      const query = search.trim().toLowerCase();
      const text = JSON.stringify(entry).toLowerCase();
      const category = getEntryCategory(entry);

      const entryDate = getDateInputValue(entry.entry_date || entry.created_at);

      const matchesFilter = activeFilter === "all" || category === activeFilter;
      const matchesSearch = !query || text.includes(query);
      const matchesDateFrom = !dateFrom || entryDate >= dateFrom;
      const matchesDateTo = !dateTo || entryDate <= dateTo;

      return matchesFilter && matchesSearch && matchesDateFrom && matchesDateTo;
    });
  }, [entries, activeFilter, search, dateFrom, dateTo]);

  useEffect(() => {
    if (!filteredEntries.length) {
      setSelectedEntryId(null);
      return;
    }

    const selectedStillExists = filteredEntries.some(
      (entry) => entry.id === selectedEntryId
    );

    if (!selectedStillExists) {
      setSelectedEntryId(filteredEntries[0].id);
    }
  }, [filteredEntries, selectedEntryId]);

  const selectedEntry = filteredEntries.find((entry) => entry.id === selectedEntryId);

  const isConnectionWallSnapshot = selectedEntry?.source_type === "connection_wall_snapshot";

  useEffect(() => {
    setNoteDraft(selectedEntry?.personal_note || "");
    setNoteMessage("");
  }, [selectedEntry?.id, selectedEntry?.personal_note]);

  function getDisplayTitle(entry) {
    if (!entry) return "";

    const category = getEntryCategory(entry);

    if (category === "connection_wall_snapshot") {
      return "Muro de Oración";
    }

    if (
      (category === "word" || category === "micro_mission") &&
      entry.lesson_day_number &&
      entry.lesson_title
    ) {
      return `Día ${entry.lesson_day_number}: ${entry.lesson_title}`;
    }

    return entry.title;
  }

  const filters = [
    { id: "all", label: "Todo" },
    { id: "micro_mission", label: "Micro-misiones" },
    { id: "word", label: "La Palabra en lo cotidiano" },
    { id: "connection_wall_snapshot", label: "Conexión" },
  ];

  function clearFilters() {
    setActiveFilter("all");
    setSearch("");
    setDateFrom("");
    setDateTo("");
  }

  async function savePersonalNote() {
    if (!selectedEntry?.id) return;

    try {
      setSavingNote(true);
      setNoteMessage("");

      const session = JSON.parse(localStorage.getItem("zedro_session"));

      const res = await fetch(`${API_URL}/api/journal/${selectedEntry.id}/note`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({
          note: noteDraft,
        }),
      });

      const json = await res.json();

      if (!res.ok || !json.ok) {
        throw new Error(json.error || "No se pudo guardar la nota");
      }

      setEntries((current) =>
        current.map((entry) =>
          entry.id === selectedEntry.id
            ? {
                ...entry,
                personal_note: json.note.note,
                personal_note_updated_at: json.note.updated_at,
              }
            : entry
        )
      );

     setNoteMessage("Nota guardada.");
    } catch (err) {
      console.error("[SAVE_PERSONAL_NOTE_ERROR]", err);
      setNoteMessage("No se pudo guardar la nota.");
    } finally {
      setSavingNote(false);
    }
  }

  function downloadConnectionWallSnapshotPdf(entry) {
    if (!entry) return;

    const snapshot = entry.response || {};

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "letter",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const margin = 36;
    const wallX = margin;
    const wallY = 96;
    const wallWidth = pageWidth - margin * 2;
    const wallHeight = pageHeight - 135;

    const dateLabel = formatDate(entry.entry_date || entry.created_at);

    function cleanText(value) {
      return String(value || "")
        .replace(/\*\*/g, "")
        .replace(/\r\n/g, "\n")
        .trim();
    }

    function addWrapped(text, x, y, width, options = {}) {
      const {
        fontSize = 8,
        fontStyle = "normal",
        color = [15, 47, 70],
        lineHeight = 10,
        maxLines = 8,
      } = options;

      const clean = cleanText(text);
      if (!clean) return y;

      doc.setFont("helvetica", fontStyle);
      doc.setFontSize(fontSize);
      doc.setTextColor(...color);

      const lines = doc.splitTextToSize(clean, width).slice(0, maxLines);

      lines.forEach((line) => {
        doc.text(line, x, y);
        y += lineHeight;
      });

      return y;
    }

    function drawRoundedRect(x, y, w, h, fill, stroke) {
      doc.setFillColor(...fill);
      doc.setDrawColor(...stroke);
      doc.roundedRect(x, y, w, h, 16, 16, "FD");
    }

    function drawCard(card, x, y, w) {
      const cardHeight = 118;

      drawRoundedRect(
        x,
        y,
        w,
        cardHeight,
        [255, 255, 255],
        [226, 230, 232]
      );

      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.5);
      doc.setTextColor(155, 117, 39);

      if (card.category) {
        doc.text(String(card.category).toUpperCase(), x + 12, y + 18);
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(15, 47, 70);

      let cursorY = y + 36;
      const titleLines = doc.splitTextToSize(cleanText(card.title), w - 24).slice(0, 2);

      titleLines.forEach((line) => {
        doc.text(line, x + 12, cursorY);
        cursorY += 10;
      });

      if (card.scripture_reference) {
        cursorY += 3;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7.5);
        doc.setTextColor(138, 105, 44);
        doc.text(cleanText(card.scripture_reference), x + 12, cursorY);
        cursorY += 12;
      }

      const body =
        card.scripture_text ||
        card.content ||
        card.prayer_text ||
        card.answered_note ||
        "";

      addWrapped(body, x + 12, cursorY, w - 24, {
        fontSize: 7.4,
        color: [34, 56, 77],
        lineHeight: 9,
        maxLines: 6,
      });

      return cardHeight + 10;
    }

    function drawColumn(column, x, y, w, h, fill) {
      drawRoundedRect(x, y, w, h, fill, [214, 178, 94]);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(15, 47, 70);
      doc.text(column.title, x + 12, y + 22);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.4);
      doc.setTextColor(66, 86, 102);

      const subtitleLines = doc
        .splitTextToSize(column.subtitle, w - 24)
        .slice(0, 2);

      let subtitleY = y + 36;
      subtitleLines.forEach((line) => {
        doc.text(line, x + 12, subtitleY);
        subtitleY += 9;
      });

      let cardY = y + 60;

      if (!column.items.length) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(66, 86, 102);
        doc.text("Sin registros en esta sección.", x + 12, cardY);
        return;
      }

      column.items.slice(0, 3).forEach((card) => {
        if (cardY + 118 < y + h - 12) {
          cardY += drawCard(card, x + 10, cardY, w - 20);
        }
      });

      if (column.items.length > 3) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(155, 117, 39);
        doc.text(`+ ${column.items.length - 3} registros más`, x + 12, y + h - 14);
      }
    }

    // Fondo general
    doc.setFillColor(248, 249, 248);
    doc.rect(0, 0, pageWidth, pageHeight, "F");

    // Encabezado
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(155, 117, 39);
    doc.text("ZEDRO ACADEMY", margin, 38);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(102, 119, 132);
    doc.text("Formación con raíz eterna", margin, 52);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(22);
    doc.setTextColor(15, 47, 70);
    doc.text("Muro de Oración", margin, 78);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(102, 119, 132);
    doc.text(`Fecha: ${dateLabel}`, pageWidth - margin, 42, { align: "right" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(66, 86, 102);
    doc.text(
      "Seguimiento diario de registros bíblicos, peticiones, oraciones y victorias.",
      margin,
      92
    );

    // Muro
    doc.setFillColor(12, 38, 58);
    doc.setDrawColor(204, 163, 82);
    doc.roundedRect(wallX, wallY, wallWidth, wallHeight, 22, 22, "FD");

    doc.setFillColor(255, 248, 232);
    doc.setDrawColor(204, 163, 82);
    doc.roundedRect(wallX + 18, wallY + 18, 104, 24, 12, 12, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(155, 117, 39);
    doc.text("CONEXIÓN ZEDRO", wallX + 32, wallY + 34);

    const columns = [
      {
        title: "Plan de Batalla",
        subtitle: "Verdades bíblicas que sostienen tu oración.",
        items: snapshot.battle_plan || [],
        fill: [242, 234, 215],
      },
      {
        title: "Peticiones activas",
        subtitle: "Lo que presentaste delante de Dios.",
        items: snapshot.petitions || [],
        fill: [223, 234, 241],
      },
      {
        title: "Oraciones",
        subtitle: "Palabras ordenadas desde tu proceso.",
        items: snapshot.prayers || [],
        fill: [236, 230, 216],
      },
      {
        title: "Victorias",
        subtitle: "Respuestas que fortalecen tu memoria.",
        items: snapshot.victories || [],
        fill: [222, 235, 231],
      },
    ];

    const gap = 12;
    const columnW = (wallWidth - 36 - gap * 3) / 4;
    const columnY = wallY + 58;
    const columnH = wallHeight - 76;

    columns.forEach((column, index) => {
      const x = wallX + 18 + index * (columnW + gap);
      drawColumn(column, x, columnY, columnW, columnH, column.fill);
    });

    // Footer
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(120, 132, 142);
    doc.text("ZEDRO Academy · Formación con raíz eterna", margin, pageHeight - 24);

    doc.save("ZEDRO-Academy-muro-de-oracion.pdf");
  }

  function downloadSelectedEntryPdf() {
  if (!selectedEntry) return;

  if (selectedEntry.source_type === "connection_wall_snapshot") {
    downloadConnectionWallSnapshotPdf(selectedEntry);
    return;
  }

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "letter",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const marginX = 56;
  const marginTop = 56;
  const marginBottom = 58;
  const contentWidth = pageWidth - marginX * 2;

  let y = marginTop;

  function footer() {
    const pageNumber = doc.internal.getNumberOfPages();

    doc.setDrawColor(225, 228, 230);
    doc.line(marginX, pageHeight - 42, pageWidth - marginX, pageHeight - 42);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 132, 142);
    doc.text("ZEDRO Academy · Formación con raíz eterna", marginX, pageHeight - 26);
    doc.text(String(pageNumber), pageWidth - marginX, pageHeight - 26, {
      align: "right",
    });
  }

  function addPageIfNeeded(extraSpace = 40) {
    if (y + extraSpace > pageHeight - marginBottom) {
      footer();
      doc.addPage();
      y = marginTop;
    }
  }

  function addWrappedText(text, options = {}) {
    const {
      fontSize = 10.5,
      fontStyle = "normal",
      color = [34, 56, 77],
      lineHeight = 15,
      spacingAfter = 10,
      indent = 0,
    } = options;

    const cleanText = cleanPdfText(text);
    if (!cleanText) return;

    const paragraphs = cleanText.split(/\n\s*\n/).filter(Boolean);

    paragraphs.forEach((paragraph, paragraphIndex) => {
      const lines = doc.splitTextToSize(paragraph.trim(), contentWidth - indent);

      lines.forEach((line) => {
        addPageIfNeeded(lineHeight + 6);

        doc.setFont("helvetica", fontStyle);
        doc.setFontSize(fontSize);
        doc.setTextColor(...color);

        doc.text(line, marginX + indent, y);
        y += lineHeight;
      });

      if (paragraphIndex < paragraphs.length - 1) {
        y += 7;
      }
    });

    y += spacingAfter;
  }

  function addSectionTitle(title) {
    addPageIfNeeded(36);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(155, 117, 39);
    doc.text(String(title || "").toUpperCase(), marginX, y);

    y += 18;

    doc.setDrawColor(232, 218, 188);
    doc.line(marginX, y, pageWidth - marginX, y);

    y += 18;
  }

  const categoryLabel = getEntryCategoryLabel(selectedEntry);
  const displayTitle =
    getEntryCategory(selectedEntry) === "micro_mission" &&
    selectedEntry.lesson_day_number &&
    selectedEntry.lesson_title
      ? `Día ${selectedEntry.lesson_day_number}: ${selectedEntry.lesson_title}`
      : getDisplayTitle(selectedEntry);
  const dateLabel = formatDate(selectedEntry.entry_date || selectedEntry.created_at);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(155, 117, 39);
  doc.text("ZEDRO ACADEMY", marginX, y);

  y += 18;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(102, 119, 132);
  doc.text("Formación con raíz eterna", marginX, y);

  y += 38;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(155, 117, 39);
  doc.text(
    `${selectedEntry.system_title || "Sistema ZEDRO Academy"} · Semana ${
      selectedEntry.week_number || "—"
    }`,
    marginX,
    y
  );

  y += 18;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(15, 47, 70);
  doc.text(categoryLabel, marginX, y);

  y += 30;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(22);
  doc.setTextColor(15, 47, 70);

  const titleLines = doc.splitTextToSize(displayTitle, contentWidth);
  titleLines.forEach((line) => {
    doc.text(line, marginX, y);
    y += 27;
  });

  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(102, 119, 132);
  doc.text(`Fecha: ${dateLabel}`, marginX, y);

  if (Number(selectedEntry.stones_earned || 0) > 0) {
    doc.text(`Piedras: +${selectedEntry.stones_earned}`, pageWidth - marginX, y, {
      align: "right",
    });
  }

  y += 32;

  const journalEntries = selectedEntry.response?.journal_entries || [];

  journalEntries.forEach((item) => {
    addSectionTitle(getBlockLabel(item.kind));

    if (item.question) {
      addWrappedText(item.question, {
        fontSize: 11,
        fontStyle: "bold",
        color: [15, 47, 70],
        lineHeight: 16,
        spacingAfter: 8,
      });
    }

    if (item.answer) {
      addWrappedText(item.answer, {
        fontSize: 10.5,
        color: [34, 56, 77],
        lineHeight: 15,
        spacingAfter: 18,
      });
    }
  });

  const personalNote = cleanPdfText(selectedEntry.personal_note);

  if (personalNote) {
    addSectionTitle("Notas personales");

    addWrappedText(personalNote, {
      fontSize: 10.5,
      color: [34, 56, 77],
      lineHeight: 15,
      spacingAfter: 18,
    });
  }

  footer();

  doc.save(buildPdfFileName(selectedEntry));
}

  return (
    <main className="bitacoraPage">
      <section className="bitacoraShell">
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="z-back-pill bitacoraBackControl"
        >
          ← Volver al panel central
        </button>

        <header className="bitacoraHeader">
          <div className="bitacoraEyebrow">Workbook personal</div>

          <h1>Bitácora ZEDRO</h1>

          <p>
            Tu biblioteca personal de proceso: Palabra, reflexiones,
            micro-misiones, evidencias, decisiones y registros construidos dentro
            del ecosistema ZEDRO Academy.
          </p>
        </header>

        <div className="bitacoraLayout">
          <aside className="bitacoraSidebar">
            <div className="bitacoraSidebarTitle">Biblioteca personal</div>

            <label className="bitacoraField">
              <span>Buscar apuntes</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por palabra..."
              />
            </label>

            <div className="bitacoraDateGrid">
              <label className="bitacoraField">
                <span>Desde</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </label>

              <label className="bitacoraField">
                <span>Hasta</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </label>
            </div>

            <div className="bitacoraFilters">
              {filters.map((filter) => {
                const active = activeFilter === filter.id;

                return (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setActiveFilter(filter.id)}
                    className={active ? "is-active" : ""}
                  >
                    {filter.label}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={clearFilters}
              className="bitacoraClearButton"
            >
              Limpiar búsqueda
            </button>
              
              <div className="bitacoraLibraryTitle">
                Registros encontrados · {filteredEntries.length}
              </div>

              {filteredEntries.length > 0 && (
                <label className="bitacoraMobileSelect">
                  <span>Elegir registro</span>

                  <select
                    value={selectedEntryId || ""}
                    onChange={(e) => setSelectedEntryId(e.target.value)}
                  >
                    {filteredEntries.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {getEntrySelectLabel(entry)}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div className="bitacoraEntryList">

              {loading && <p className="bitacoraEmptyText">Cargando bitácora...</p>}

              {!loading && filteredEntries.length === 0 && (
                <p className="bitacoraEmptyText">
                  No hay registros para esta búsqueda.
                </p>
              )}

              {filteredEntries.map((entry) => {
                const active = selectedEntryId === entry.id;

                return (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => setSelectedEntryId(entry.id)}
                    className={`bitacoraEntryButton ${active ? "is-active" : ""}`}
                  >
                    <span className="bitacoraEntryType">
                      {getEntryCategoryLabel(entry)}
                    </span>

                    <strong>{entry.title}</strong>

                    {entry.lesson_title && (
                      <small>
                        Día {entry.lesson_day_number}: {entry.lesson_title}
                      </small>
                    )}

                    <em>{formatDate(entry.entry_date || entry.created_at)}</em>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="bitacoraDetail">
            {!loading && !selectedEntry && (
              <div className="bitacoraEmptyPanel">
                <div className="bitacoraEyebrow">Sin selección</div>
                <h2>No hay registros para mostrar</h2>
                <p>
                  Ajusta la búsqueda, cambia los filtros o completa una actividad
                  dentro de ZEDRO para alimentar tu Bitácora.
                </p>
              </div>
            )}

            {selectedEntry && (
              <article className="bitacoraDetailCard">
                <div className="bitacoraDetailTop">
                  <div>
                    <div className="bitacoraDetailMeta">
                      {selectedEntry.system_title || "ZEDRO"} · Semana{" "}
                      {selectedEntry.week_number || "—"}
                    </div>

                    <div className="bitacoraDetailCategory">
                      {getEntryCategoryLabel(selectedEntry)}
                    </div>

                    <h2>{getDisplayTitle(selectedEntry)}</h2>

                    {selectedEntry?.prompt && !isConnectionWallSnapshot && (
                      <p className="bitacoraPrompt">{selectedEntry.prompt}</p>
                    )}

                    {isConnectionWallSnapshot && (
                      <p className="bitacoraWallIntroText">
                         Lleva el seguimiento diario de tu Muro de Oración con los registros bíblicos,
                         peticiones, oraciones y victorias diarias registradas en Bitácora.
                      </p>
                    )}

                    {selectedEntry.lesson_title && 
                      !["word", "micro_mission"].includes(getEntryCategory(selectedEntry)) && (
                        <p className="bitacoraLesson">
                          Día {selectedEntry.lesson_day_number}: {selectedEntry.lesson_title}
                        </p>
                    )}
                  </div>

                  <div className="bitacoraDetailDate">
                    <span>
                      {formatDate(selectedEntry.entry_date || selectedEntry.created_at)}
                    </span>

                    {Number(selectedEntry.stones_earned || 0) > 0 && (
                      <strong>+{selectedEntry.stones_earned || 0} piedras</strong>
                    )}
                  </div>
                </div>

                <div className="bitacoraDownloadRow">
                  <button
                   type="button"
                   onClick={downloadSelectedEntryPdf}
                   className="bitacoraDownloadButton"
                  >
                   Descargar PDF
                 </button>
               </div>
                
                {renderResponse(selectedEntry.response, selectedEntry)}

                {!isConnectionWallSnapshot && (
                  <section className="bitacoraPersonalNotes">
                    <div className="bitacoraPersonalNotesHeader">
                      <div>
                        <span>Notas personales</span>
                        <h3>Mis apuntes sobre este registro</h3>
                      </div>
                   </div>

                    <textarea
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder="Escribe aquí tus notas personales sobre este día, esta micro-misión o esta Palabra trabajada..."
                    />

                    <div className="bitacoraPersonalNotesActions">
                      <button
                        type="button"
                        onClick={savePersonalNote}
                        disabled={savingNote}
                      >
                        {savingNote ? "Guardando..." : "Guardar nota"}
                      </button>

                      {noteMessage && <p>{noteMessage}</p>}
                    </div>
                  </section>
                )}  
              </article>
            )}
          </section>
        </div>
      </section>

      <style>
        {`
          .bitacoraPage {
            width: 100%;
            max-width: 100%;
            min-height: 100vh;
            overflow-x: hidden;
            background: var(--page-bg);
            padding: 2rem 1.25rem 4rem;
            box-sizing: border-box;
          }

          .bitacoraShell {
            width: 100%;
            max-width: 1240px;
            margin: 0 auto;
            box-sizing: border-box;
          }

          .bitacoraBackButton {
            margin-bottom: 1.5rem;
            padding: 0.55rem 0.9rem;
            border-radius: 999px;
            border: 1px solid rgba(15, 47, 70, 0.12);
            background: #fff;
            color: #0f2f46;
            cursor: pointer;
            font-weight: 700;
            font-family: Questrial, sans-serif;
          }

          .bitacoraBackControl {
            margin-bottom: 1.25rem;
          }

          .bitacoraHeader {
            margin-bottom: 2rem;
            padding-top: 0.25rem;
          }

          .bitacoraEyebrow {
            display: inline-flex;
            margin-bottom: 0.8rem;
            padding: 0.38rem 0.75rem;
            border-radius: 999px;
            background: rgba(204, 163, 82, 0.12);
            color: #9b7527;
            font-size: 0.72rem;
            font-weight: 800;
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }

          .bitacoraHeader h1 {
            margin: 0;
            color: #0f2f46;
            font-size: clamp(2rem, 4vw, 3rem);
            font-weight: 400;
            letter-spacing: -0.04em;
            line-height: 1.05;
          }

          .bitacoraHeader p {
            margin: 0.9rem 0 0;
            max-width: 760px;
            color: rgba(15, 47, 70, 0.68);
            font-size: 1rem;
            line-height: 1.65;
          }

          .bitacoraLayout {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            display: grid;
            grid-template-columns: 280px minmax(0, 1fr);
            gap: 1.25rem;
            align-items: start;
            box-sizing: border-box;
          }

          .bitacoraSidebar {
            position: sticky;
            top: 96px;
            max-height: none;
            overflow: visible;
            display: flex;
            flex-direction: column;
            padding: 1.05rem 1rem 1.35rem;
            border-radius: 28px;
            background: #fff;
            border: 1px solid rgba(15, 47, 70, 0.08);
            box-shadow: 0 18px 45px rgba(15, 47, 70, 0.06);
            box-sizing: border-box;
          }

          .bitacoraSidebarTitle,
          .bitacoraLibraryTitle {
            color: #9b7527;
            font-size: 0.72rem;
            font-weight: 800;
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }

          .bitacoraSidebarTitle {
            margin-bottom: 0.9rem;
          }

          .bitacoraField {
            display: grid;
            gap: 0.35rem;
          }

          .bitacoraField span {
            color: rgba(15, 47, 70, 0.62);
            font-size: 0.76rem;
            font-weight: 800;
          }

          .bitacoraField input {
            width: 100%;
            min-width: 0;
            box-sizing: border-box;
            padding: 0.72rem 0.78rem;
            border-radius: 15px;
            border: 1px solid rgba(15, 47, 70, 0.12);
            outline: none;
            color: #0f2f46;
            background: #fff;
            font-family: Questrial, sans-serif;
            font-size: 0.92rem;
          }

          .bitacoraDateGrid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 0.6rem;
            margin-top: 0.8rem;
          }

          .bitacoraFilters {
            display: grid;
            gap: 0.45rem;
            margin-top: 1rem;
          }

          .bitacoraFilters button,
          .bitacoraClearButton {
            width: 100%;
            text-align: left;
            padding: 0.72rem 0.82rem;
            border-radius: 15px;
            cursor: pointer;
            font-family: Questrial, sans-serif;
          }

          .bitacoraFilters button {
            border: 1px solid transparent;
            background: rgba(15, 47, 70, 0.035);
            color: rgba(15, 47, 70, 0.68);
            font-weight: 600;
          }

          .bitacoraFilters button.is-active {
            border: 1px solid rgba(204, 163, 82, 0.35);
            background: rgba(204, 163, 82, 0.12);
            color: #0f2f46;
            font-weight: 800;
          }

          .bitacoraClearButton {
            margin-top: 0.9rem;
            margin-bottom: 0.25rem;
            border: 1px solid rgba(204, 163, 82, 0.32);
            background: linear-gradient(180deg, rgba(204, 163, 82, 0.10), #ffffff);
            color: #7b6430;
            font-weight: 900;
            letter-spacing: 0.02em;
            text-align: center;
          }

          .bitacoraClearButton:hover {
            background: rgba(49, 46, 40, 0.14);
            border-color: rgba(204, 163, 82, 0.46);
          }

          .bitacoraLibraryTitle {
            margin: 1.35rem 0 0.7rem;
            padding-top: 1rem;
            border-top: 1px solid rgba(15, 47, 70, 0.08);
          }

          .bitacoraEntryList {
           display: grid;
           gap: 0.55rem;
           max-height: 210px;
           overflow: auto;
           padding-right: 0.25rem;
          }

          .bitacoraEntryButton {
            width: 100%;
            text-align: left;
            padding: 0.9rem;
            border-radius: 18px;
            border: 1px solid rgba(15, 47, 70, 0.07);
            background: rgba(15, 47, 70, 0.025);
            cursor: pointer;
            font-family: Questrial, sans-serif;
          }

          .bitacoraEntryButton.is-active {
            background: #fff;
            border-color: rgba(204, 163, 82, 0.38);
            box-shadow: 0 12px 28px rgba(15, 47, 70, 0.08);
          }

          .bitacoraEntryButton span,
          .bitacoraEntryButton strong,
          .bitacoraEntryButton small,
          .bitacoraEntryButton em {
            display: block;
          }

          .bitacoraEntryType {
            margin-bottom: 0.35rem;
            color: #9b7527;
            font-size: 0.68rem;
            font-weight: 900;
            letter-spacing: 0.09em;
            text-transform: uppercase;
          }

          .bitacoraEntryButton strong {
            color: #0f2f46;
            font-size: 0.95rem;
            line-height: 1.25;
            margin-bottom: 0.25rem;
          }

          .bitacoraEntryButton small {
            color: rgba(15, 47, 70, 0.62);
            font-size: 0.82rem;
            line-height: 1.35;
          }

          .bitacoraEntryButton em {
            margin-top: 0.45rem;
            color: rgba(15, 47, 70, 0.48);
            font-size: 0.78rem;
            font-style: normal;
          }

          .bitacoraDetail {
            min-width: 0;
          }

          .bitacoraDetailCard,
          .bitacoraEmptyPanel {
            padding: clamp(1.3rem, 3vw, 2rem);
            border-radius: 30px;
            background: #fff;
            border: 1px solid rgba(15, 47, 70, 0.08);
            box-shadow: 0 18px 45px rgba(15, 47, 70, 0.06);
          }

          .bitacoraEmptyPanel h2 {
            margin: 0;
            color: #0f2f46;
            font-weight: 500;
          }

          .bitacoraEmptyPanel p {
            max-width: 520px;
            color: rgba(15, 47, 70, 0.68);
            line-height: 1.65;
          }

          .bitacoraDetailTop {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            flex-wrap: wrap;
            margin-bottom: 1.2rem;
          }

          .bitacoraDetailMeta,
          .bitacoraDetailCategory {
            color: #9b7527;
            font-size: 0.72rem;
            font-weight: 800;
            letter-spacing: 0.1em;
            text-transform: uppercase;
          }

          .bitacoraDetailCategory {
            margin-top: 0.35rem;
            color: rgba(15, 47, 70, 0.5);
          }

          .bitacoraDetailCard h2 {
            margin: 0.45rem 0 0;
            color: #0f2f46;
            font-size: clamp(1.45rem, 3vw, 2.2rem);
            font-weight: 400;
            letter-spacing: -0.035em;
            line-height: 1.1;
          }

          .bitacoraLesson {
            margin: 0.45rem 0 0;
            color: rgba(15, 47, 70, 0.62);
            line-height: 1.55;
          }

          .bitacoraDetailDate {
            text-align: right;
            color: rgba(15, 47, 70, 0.62);
            font-size: 0.9rem;
          }

          .bitacoraDetailDate span,
          .bitacoraDetailDate strong {
            display: block;
          }

          .bitacoraDetailDate strong {
            margin-top: 0.35rem;
            color: #0f2f46;
          }

          .bitacoraPrompt {
            margin: 0 0 1.2rem;
            color: rgba(15, 47, 70, 0.72);
            line-height: 1.65;
          }

          .bitacoraResponseGrid {
            display: grid;
            gap: 1rem;
          }

          .bitacoraResponseCard {
            padding: 1rem;
            border-radius: 20px;
            background: rgba(15, 47, 70, 0.04);
            border: 1px solid rgba(15, 47, 70, 0.06);
          }

          .bitacoraResponseCard.is-rule {
            background: rgba(204, 163, 82, 0.08);
            border-color: rgba(204, 163, 82, 0.22);
          }

          .bitacoraResponseLabel {
            margin-bottom: 0.5rem;
            color: #9b7527;
            font-size: 0.74rem;
            font-weight: 800;
            letter-spacing: 0.08em;
            text-transform: uppercase;
          }

          .bitacoraResponseQuestion {
            margin: 0 0 0.75rem;
            color: #0f2f46;
            line-height: 1.55;
            font-weight: 600;
          }

          .bitacoraResponseAnswer {
            color: #22384d;
            line-height: 1.6;
            font-size: 0.96rem;
            background: #fff;
            border-radius: 14px;
            padding: 0.85rem 1rem;
            white-space: normal;
            overflow-wrap: anywhere;
          }

          .bitacoraEmptyText {
            color: rgba(15, 47, 70, 0.62);
            line-height: 1.55;
          }

          .bitacoraMobileSelect {
            display: none;
          }

          .bitacoraPersonalNotes {
            margin-top: 1.25rem;
            padding: 1.15rem;
            border-radius: 22px;
            background: linear-gradient(180deg, rgba(204, 163, 82, 0.08), rgba(255, 255, 255, 0.95));
            border: 1px solid rgba(204, 163, 82, 0.22);
          }

          .bitacoraPersonalNotesHeader {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 0.85rem;
          }

          .bitacoraPersonalNotesHeader span {
            display: block;
            margin-bottom: 0.25rem;
            color: #9b7527;
            font-size: 0.72rem;
            font-weight: 900;
            letter-spacing: 0.1em;
            text-transform: uppercase;
          }

          .bitacoraPersonalNotesHeader h3 {
            margin: 0;
            color: #0f2f46;
            font-size: 1.15rem;
            font-weight: 500;
          }

          .bitacoraPersonalNotes textarea {
            width: 100%;
            min-height: 150px;
            resize: vertical;
            box-sizing: border-box;
            padding: 1rem;
            border-radius: 18px;
            border: 1px solid rgba(15, 47, 70, 0.12);
            background: #fff;
            color: #22384d;
            font-family: Questrial, sans-serif;
            font-size: 1rem;
            line-height: 1.65;
            outline: none;
          }

          .bitacoraPersonalNotes textarea:focus {
            border-color: rgba(204, 163, 82, 0.42);
            box-shadow: 0 0 0 4px rgba(204, 163, 82, 0.08);
          }

          .bitacoraPersonalNotesActions {
            display: flex;
            align-items: center;
            gap: 0.9rem;
            flex-wrap: wrap;
            margin-top: 0.85rem;
          }

          .bitacoraPersonalNotesActions button {
            min-height: 42px;
            padding: 0 1.05rem;
            border-radius: 999px;
            border: 1px solid rgba(204, 163, 82, 0.34);
            background: #fff;
            color: #7b6430;
            font-family: Questrial, sans-serif;
            font-weight: 900;
            cursor: pointer;
          }

          .bitacoraPersonalNotesActions button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .bitacoraPersonalNotesActions p {
            margin: 0;
            color: rgba(15, 47, 70, 0.62);
            font-size: 0.9rem;
          }

          .bitacoraDownloadRow {
            display: flex;
            justify-content: flex-end;
            margin: 1rem 0 1.2rem;
          }

          .bitacoraDownloadButton {
            min-height: 42px;
            padding: 0 1.1rem;
            border-radius: 999px;
            border: 1px solid rgba(204, 163, 82, 0.38);
            background: linear-gradient(180deg, rgba(204, 163, 82, 0.12), #ffffff);
            color: #7b6430;
            font-family: Questrial, sans-serif;
            font-size: 0.94rem;
            font-weight: 900;
            cursor: pointer;
            box-shadow: 0 10px 24px rgba(15, 47, 70, 0.04);
          }

          .bitacoraDownloadButton:hover {
            border-color: rgba(204, 163, 82, 0.52);
            box-shadow: 0 14px 30px rgba(15, 47, 70, 0.08);
          }

          .bitacoraWallSnapshot {
            border-radius: 30px;
            border: 1px solid rgba(204, 163, 82, 0.32);
            background:
              radial-gradient(circle at 24% 10%, rgba(227, 197, 112, 0.18), transparent 18%),
              radial-gradient(circle at 78% 12%, rgba(58, 89, 120, 0.14), transparent 18%),
              linear-gradient(135deg, #0c263a 0%, #13354d 42%, #0f2f46 72%, #081f31 100%);
            padding: 1.1rem;
            box-shadow: 0 22px 55px rgba(6, 18, 28, 0.16);
          }

          .bitacoraWallSnapshotHead {
            margin-bottom: 0.85rem;
          }

          .bitacoraWallSnapshotHead span {
            display: inline-flex;
            margin-bottom: 0.55rem;
            padding: 0.4rem 0.75rem;
            border-radius: 999px;
            background: rgba(255, 248, 232, 0.14);
            border: 1px solid rgba(204, 163, 82, 0.28);
            color: #e3c570;
            font-size: 0.72rem;
            font-weight: 900;
            letter-spacing: 0.11em;
            text-transform: uppercase;
          }

          .bitacoraWallSnapshotHead h3 {
            margin: 0;
            color: #ffffff;
            font-size: 1.45rem;
            font-weight: 700;
            letter-spacing: -0.035em;
          }

          .bitacoraWallSnapshotHead p {
            margin: 0.45rem 0 0;
            color: rgba(255, 255, 255, 0.72);
            font-size: 0.92rem;
            line-height: 1.55;
          }

          .bitacoraWallGrid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 0.85rem;
          }

          .bitacoraWallColumn {
            min-height: 320px;
            padding: 0.7rem;
            border-radius: 22px;
            border: 1px solid rgba(204, 163, 82, 0.34);
            background:
              radial-gradient(circle at top left, rgba(204, 163, 82, 0.13), transparent 38%),
              linear-gradient(180deg, rgba(244, 239, 225, 0.96), rgba(255, 255, 255, 0.78));
          }

          .bitacoraWallColumn--petitions {
            background:
              radial-gradient(circle at top left, rgba(15, 47, 70, 0.11), transparent 40%),
              linear-gradient(180deg, rgba(226, 236, 240, 0.96), rgba(255, 255, 255, 0.78));
          }

          .bitacoraWallColumn--prayers {
            background:
              radial-gradient(circle at top left, rgba(204, 163, 82, 0.12), transparent 38%),
              linear-gradient(180deg, rgba(239, 234, 220, 0.98), rgba(255, 255, 255, 0.78));
          }

          .bitacoraWallColumn--victories {
            background:
              radial-gradient(circle at top left, rgba(39, 91, 76, 0.12), transparent 40%),
              linear-gradient(180deg, rgba(226, 239, 232, 0.96), rgba(255, 255, 255, 0.78));
          }

          .bitacoraWallColumnHead h4 {
            margin: 0;
            color: #0f2f46;
            font-size: 0.98rem;
            font-weight: 900;
          }

          .bitacoraWallColumnHead p {
            margin: 0.28rem 0 0.75rem;
            color: rgba(15, 47, 70, 0.58);
            font-size: 0.8rem;
            line-height: 1.4;
          }

          .bitacoraWallCards {
            display: grid;
            gap: 0.7rem;
          }

          .bitacoraWallCard {
            padding: 0.72rem;
            border-radius: 16px;
            background: rgba(255, 255, 255, 0.86);
            border: 1px solid rgba(15, 47, 70, 0.08);
            box-shadow: 0 10px 22px rgba(15, 47, 70, 0.05);
          }

          .bitacoraWallCardTop {
            display: flex;
            justify-content: space-between;
            gap: 0.45rem;
            margin-bottom: 0.45rem;
            color: #9b7527;
            font-size: 0.62rem;
            font-weight: 900;
            letter-spacing: 0.06em;
            line-height: 1.25;
            text-transform: uppercase;
          }

          .bitacoraWallCardTop em {
            font-style: normal;
            color: rgba(15, 47, 70, 0.52);
          }

          .bitacoraWallCard h4 {
            margin: 0 0 0.45rem;
            color: #0f2f46;
            font-size: 0.94rem;
            font-weight: 900;
            line-height: 1.3;
          }

          .bitacoraWallReference {
            margin: 0 0 0.45rem;
            color: #8a692c;
            font-size: 0.86rem;
            font-weight: 900;
          }

          .bitacoraWallText,
          .bitacoraWallPrayer p,
          .bitacoraWallVictory p {
            margin: 0;
            color: rgba(15, 47, 70, 0.75);
            font-size: 0.82rem;
            line-height: 1.48;
          }

          .bitacoraWallPrayer,
          .bitacoraWallVictory {
            margin-top: 0.65rem;
            padding: 0.7rem;
            border-radius: 14px;
            background: rgba(15, 47, 70, 0.04);
          }

          .bitacoraWallPrayer strong,
          .bitacoraWallVictory strong {
            display: block;
            margin-bottom: 0.35rem;
            color: #0f2f46;
            font-size: 0.78rem;
            font-weight: 900;
          }

          .bitacoraWallEmpty {
            margin: 0;
            color: rgba(15, 47, 70, 0.56);
            font-size: 0.84rem;
            line-height: 1.5;
          }

          .bitacoraWallIntroText {
            max-width: 620px;
            margin: 0.75rem 0 0;
            color: rgba(15, 47, 70, 0.68);
            font-size: 0.98rem;
            line-height: 1.6;
          }

                    @media (max-width: 940px) {
            .bitacoraPage {
              width: 100%;
              max-width: 100%;
              overflow-x: hidden;
              padding: 1.35rem 0.9rem 3rem;
              box-sizing: border-box;
            }

            .bitacoraShell {
              width: 100%;
              max-width: 100%;
              min-width: 0;
              box-sizing: border-box;
            }

            .bitacoraLayout {
              width: 100%;
              max-width: 100%;
              min-width: 0;
              grid-template-columns: minmax(0, 1fr);
              gap: 1rem;
              box-sizing: border-box;
            }

            .bitacoraSidebar,
            .bitacoraDetail,
            .bitacoraDetailCard,
            .bitacoraEmptyPanel {
              width: 100%;
              max-width: 100%;
              min-width: 0;
              box-sizing: border-box;
            }

            .bitacoraSidebar {
              position: relative;
              top: auto;
              max-height: none;
              overflow: visible;
              padding: 1.05rem;
              border-radius: 26px;
            }

            .bitacoraDetailDate {
              text-align: left;
            }

            .bitacoraHeader h1 {
              font-size: clamp(2.15rem, 10vw, 3.1rem);
            }

            .bitacoraHeader p {
              font-size: 1.05rem;
              line-height: 1.75;
            }

            .bitacoraField input,
            .bitacoraFilters button,
            .bitacoraClearButton,
            .bitacoraEntryButton,
            .bitacoraMobileSelect select {
              width: 100%;
              max-width: 100%;
              min-width: 0;
              box-sizing: border-box;
            }

            .bitacoraFilters button {
              white-space: normal;
              line-height: 1.35;
            }

            .bitacoraMobileSelect {
              display: grid;
              gap: 0.4rem;
              margin-bottom: 0.9rem;
            }

            .bitacoraMobileSelect span {
              color: rgba(15, 47, 70, 0.62);
              font-size: 0.85rem;
              font-weight: 800;
            }

            .bitacoraMobileSelect select {
              min-height: 3rem;
              padding: 0 0.75rem;
              border-radius: 16px;
              border: 1px solid rgba(204, 163, 82, 0.32);
              background: #fff;
              color: #0f2f46;
              font-family: Questrial, sans-serif;
              font-size: 1rem;
              font-weight: 700;
              line-height: 1.25;
            }

            .bitacoraEntryList {
              display: none;
            }

            .bitacoraWallGrid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }

          @media (max-width: 560px) {
            .bitacoraPage {
              padding: 1rem 0.9rem 2.75rem;
            }

            .bitacoraBackButton {
              font-size: 0.95rem;
              padding: 0.68rem 1rem;
            }

            .bitacoraEyebrow {
              font-size: 0.72rem;
                letter-spacing: 0.13em;
            }

            .bitacoraHeader {
              margin-bottom: 1.35rem;
            }

            .bitacoraHeader h1 {
              font-size: 2.55rem;
               letter-spacing: -0.055em;
            }

            .bitacoraHeader p {
              font-size: 1.05rem;
              line-height: 1.72;
            }

            .bitacoraDateGrid {
              grid-template-columns: 1fr;
            }

            .bitacoraSidebar,
            .bitacoraDetailCard,
            .bitacoraEmptyPanel {
              border-radius: 24px;
            }

            .bitacoraSidebar {
              padding: 1rem;
            }

            .bitacoraField input {
              height: 3.25rem;
              font-size: 1rem;
              -webkit-appearance: none;
              appearance: none;
            }

            .bitacoraFilters button,
            .bitacoraClearButton {
              font-size: 1rem;
              padding: 0.9rem 1rem;
            }

            .bitacoraEntryButton {
              padding: 1rem;
            }

            .bitacoraEntryButton strong {
              font-size: 1.05rem;
            }

            .bitacoraEntryButton small {
              font-size: 0.95rem;
            }

            .bitacoraDetailCard,
            .bitacoraEmptyPanel {
              padding: 1.15rem;
            }

            .bitacoraDetailCard h2 {
              font-size: 2rem;
            }

            .bitacoraLesson,
            .bitacoraPrompt {
              font-size: 1.05rem;
              line-height: 1.7;
            }

            .bitacoraResponseCard {
              padding: 1rem;
              border-radius: 20px;
            }

            .bitacoraResponseQuestion {
              font-size: 1.08rem;
              line-height: 1.58;
            }

            .bitacoraResponseAnswer {
              font-size: 1.03rem;
              line-height: 1.65;
              padding: 0.95rem;
            }

            .bitacoraDownloadRow {
              justify-content: stretch;
            }

            .bitacoraDownloadButton {
              width: 100%;
            }

            .bitacoraWallGrid {
              grid-template-columns: 1fr;
            }

            .bitacoraWallColumn {
              min-height: auto;
            }

            .bitacoraWallSnapshot {
              padding: 0.85rem;
              border-radius: 24px;
            }

           }
        `}
      </style>
    </main>
  );
}